import React, { useEffect, useRef } from 'react';
import './ProgressIndicator.css';

/**
 * ProgressIndicator Component
 * Animated progress bar with customizable duration and completion callback
 * 
 * @param {number} progress - Progress value (0-100)
 * @param {number} duration - Animation duration in milliseconds (default: 10000)
 * @param {boolean} monochrome - Use monochrome/greyscale animation instead of colorful (default: false)
 * @param {string} className - Additional CSS classes
 * @param {function} onComplete - Callback when progress reaches 100%
 */
const ProgressIndicator = ({ 
  progress = 0, 
  duration = 10000,
  monochrome = false,
  className = '', 
  onComplete 
}) => {
  const indicatorRef = useRef(null);
  const previousProgressRef = useRef(progress);

  useEffect(() => {
    const indicator = indicatorRef.current;
    if (!indicator) return;

    const previousProgress = previousProgressRef.current;
    
    // If progress reset to 0 or decreased significantly, reset immediately
    if (previousProgress === undefined || progress < previousProgress - 5 || progress === 0) {
      // Reset to 0% immediately without transition
      indicator.style.transition = 'none';
      indicator.style.width = '0%';
      
      // If progress is being reset, wait a moment then start smooth updates
      if (progress === 0) {
        previousProgressRef.current = progress;
        return;
      }
      
      // Start smooth animation for new progress
      requestAnimationFrame(() => {
        indicator.style.transition = 'width 0.1s linear';
        indicator.style.width = `${progress}%`;
      });
    } else {
      // Continuous update - update width smoothly
      indicator.style.transition = 'width 0.1s linear';
      indicator.style.width = `${progress}%`;
    }
    
    previousProgressRef.current = progress;
    
    // Call onComplete when progress reaches 100%
    if (progress === 100 && onComplete) {
      const completeTimeoutId = setTimeout(() => {
        onComplete();
      }, 100);
      
      return () => clearTimeout(completeTimeoutId);
    }
  }, [progress, duration, onComplete]);

  const monochromeClass = monochrome ? 'progress-indicator-monochrome' : '';

  return (
    <div 
      ref={indicatorRef}
      className={`progress-indicator ${monochromeClass} ${className}`.trim()}
      style={{ width: '0%' }}
    />
  );
};

export default ProgressIndicator;














